import { useState, useEffect, useRef } from 'react';
import dayjs from 'utils/dayjs';
import { fromInstant, nowInPreferenceTz } from 'utils/timezone';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import ErrorOutline from '@mui/icons-material/ErrorOutlineOutlined';
import ClosedCaptionDisabledOutlined from '@mui/icons-material/ClosedCaptionDisabledOutlined';
import ClosedCaptionOutlined from '@mui/icons-material/ClosedCaptionOutlined';
import MicNoneOutlined from '@mui/icons-material/MicNoneOutlined';
import MicOffOutlined from '@mui/icons-material/MicOffOutlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import VideocamOffOutlined from '@mui/icons-material/VideocamOffOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import { connect } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import * as Sentry from '@sentry/browser';
import Fullscreen from '../../global/Fullscreen';
import * as utils from './utils';
import VideoBar from './VideoBar';
import Panel from '../panel';
import InfoModal from './InfoModal';
import {
  fetchAppointment,
  fetchAppointmentNotes,
  updateAppointmentSession,
  fetchAppointmentAssessments,
} from '../../../actions/sessions';
import { fetchClient } from '../../../actions/clients';
import { fetchUser } from '../../../actions/user';
import { updateAppointment } from '../../../ducks/sessions/appointment';
import { fetchTodayAppointments } from '../../../ducks/dashboard/todayAppointments';
import TextChat from './text_chat/TextChat';
import TwilioAudio from '../panel/TwilioAudio';
import { fetchDocuments } from '../../../actions/process';
import LoadingPanel from '../../global/LoadingPanel';
import TwilioVideo from './TwilioVideo';
import VonageVideo from './Vonage/VonageVideo';
import PractitionerVonagePreCall from './PractitionerVonagePreCall';
import { sharedDeviceMessage } from '../vonage/device_settings/messages';
import LiveCaptionsPanel from '../vonage/live_captions/LiveCaptionsPanel';

export const PractitionerVideo = incomingProps => {
  const props = {
    ...{
      appointment: {},
      allAppointmentNotes: [],
      clientData: {},
      me: {},
      clientID: null,
    },
    ...incomingProps,
  };
  // Incoming undefined from Redux overwrites defaults above — coalesce again.
  const appointment = props.appointment || {};
  const me = props.me || {};
  const {
    match,
    clientData = {},
    allAppointmentNotes = [],
    clientName,
    counsellorName,
    intl,
    clientID,
  } = props;

  const [countdown, setCountdown] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [showChatBox, setShowChatBox] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(true);
  const [audioOpen, setAudioOpen] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [videoProvider, setVideoProvider] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [preCallSettings, setPreCallSettings] = useState(null);
  const [vonageDeviceSettings, setVonageDeviceSettings] = useState(null);
  const [liveCaptions, setLiveCaptions] = useState(null);
  const intervalID = useRef(null);
  const activeVideoProvider = appointment.video_provider || videoProvider;

  // twilio control functions
  const twilioControl = useRef({
    toggleCamera: () => {},
    toggleMic: () => {},
  });

  useEffect(() => {
    const appointmentId = match.params.id;
    setPreCallSettings(null);
    setVonageDeviceSettings(null);
    setLiveCaptions(null);
    setCameraOpen(true);
    setAudioOpen(true);

    props.fetchAppointment(
      appointmentId,
      apptResponse => {
        const fetchedAppointment = apptResponse.data;
        const client = fetchedAppointment?.client;

        if (!fetchedAppointment || !client?.id) {
          Sentry.captureException(
            new Error(
              `Video session missing appointment/client for id=${appointmentId}`,
            ),
          );
          setConnectionError(true);
          return;
        }

        setVideoProvider(fetchedAppointment.video_provider);

        if (fetchedAppointment.status === 'scheduled') {
          props.updateAppointment(appointmentId, { status: 'pending' });
        }

        const cId = client.id;
        const now = fromInstant(new Date());
        const end = dayjs.utc(fetchedAppointment.end_date);

        const ms = end.diff(now);

        startCountdown(ms);

        props.fetchUser(userResponse => {
          const userId =
            _.get(userResponse, 'data.id') || _.get(props, 'me.id') || (me && me.id);
          const appointmentSession = _.find(
            fetchedAppointment.appointment_sessions,
            { user_id: userId },
          );

          if (!appointmentSession?.id) {
            Sentry.captureException(
              new Error(
                `Video session missing appointment_session for appointment=${appointmentId} user=${userId}`,
              ),
            );
            setConnectionError(true);
            return;
          }

          props.updateAppointmentSession(appointmentSession.id, {
            joined_yn: true,
            source: 'web',
          });

          props.fetchClient(cId);
          props.fetchAppointmentNotes(cId);
        });
      },
      error => {
        Sentry.captureException(error);
        setConnectionError(true);
      },
    );

    props.fetchAppointmentAssessments(appointmentId, response => {
      setAssessments(response.data);
    });

    return () => clearInterval(intervalID.current);
  }, [match.params.id]);

  const disconnectAll = () => {
    clearInterval(intervalID.current);
    utils.pushThem(props);
  };

  const startCountdown = time => {
    let newTime = time;
    const interval = 1000;
    const oneHour = 3600000;
    const endTime = fromInstant(appointment.end_date);

    intervalID.current = setInterval(() => {
      newTime -= interval;
      const d = dayjs.duration(newTime);
      const s = Math.floor(d.asHours()) + dayjs.utc(newTime).format(':mm');

      setCountdown(s);

      if (newTime < 0) {
        const timeOver = nowInPreferenceTz().diff(endTime, 'minutes');
        const negativeRemainder =
          timeOver >= 10 ? `-0:${timeOver}` : `-0:0${timeOver}`;
        setCountdown(negativeRemainder);
        setSnackbarOpen(true);
      }

      if (newTime < -oneHour) {
        clearInterval(intervalID.current);
        disconnectAll();
      }
    }, interval);
  };

  const videoScreen = () => {
    if (activeVideoProvider === 'twilio') {
      return (
        <TwilioVideo
          channel={match.params.id}
          userId={userId}
          callbackForTwillio={callbackForTwillio}
          sessionType={providerType}
          appointment={appointment}
        />
      );
    }
    if (activeVideoProvider === 'vonage') {
      return (
        <VonageVideo
          channel={match.params.id}
          userId={userId}
          callbackForControls={callbackForTwillio}
          sessionType={providerType}
          appointment={appointment}
          user={me}
          initialSettings={preCallSettings}
          onDeviceSettingsReady={setVonageDeviceSettings}
          onLiveCaptionsReady={setLiveCaptions}
        />
      );
    }

    return null;
  };

  const toggleCamera = () => {
    const changed = twilioControl.current.toggleCamera();
    if (changed === false) return;
    setCameraOpen(prev => !prev);
  };

  const toggleMic = () => {
    twilioControl.current.toggleMic();
    setAudioOpen(prev => !prev);
  };

  const callbackForTwillio = controls => {
    if (controls.toggleCamera) {
      twilioControl.current.toggleCamera = controls.toggleCamera;
    }
    if (controls.toggleMic) {
      twilioControl.current.toggleMic = controls.toggleMic;
    }
  };

  const completePreCall = settings => {
    setPreCallSettings(settings);
    setCameraOpen(Boolean(settings.cameraDeviceId && settings.isCameraEnabled));
    setAudioOpen(settings.isMicrophoneEnabled);
  };

  const toggleLiveCaptions = () => {
    if (!liveCaptions?.available || liveCaptions.isStarting) return;
    liveCaptions.onChange(!liveCaptions.isEnabled);
  };

  const resourceId = appointment.id;
  const { provider_type: providerType } = appointment;
  const userId = _.get(me, 'id');
  const timezone = me.preference ? me.preference.timezone : 'America/Toronto';
  const receivers = [];

  if (providerType === 'couples') {
    const host = appointment.couples_appointment_detail?.host;
    const guest = appointment.couples_appointment_detail?.guest;
    if (host) receivers.push(host);
    if (guest) receivers.push(guest);
  } else if (appointment.client) {
    receivers.push(appointment.client);
  }

  if (connectionError) {
    return (
      <div className="global-loading-panel">
        <div className="panel-wrapper">
          <div className="panel-message">
            {intl.formatMessage({
              defaultMessage:
                'We were unable to connect you to the session. Please refresh the page to try again.',
            })}
          </div>
        </div>
      </div>
    );
  }

  if (
    !appointment.id ||
    !me.id ||
    String(appointment.id) !== String(match.params.id)
  ) {
    return <LoadingPanel />;
  }

  if (activeVideoProvider === 'vonage' && !preCallSettings) {
    return (
      <PractitionerVonagePreCall
        appointment={appointment}
        user={me}
        locale={intl.locale || 'en'}
        onComplete={completePreCall}
        onCancel={() => utils.pushThem(props)}
      />
    );
  }

  return (
    <div id="practitioner-video" className="row">
      <div
        style={{ paddingTop: '20px' }}
        className="col-lg-7 col-md-12 col-sm-12"
      >
        <Fullscreen enabled={isFull} onChange={setIsFull}>
          <div
            className={`parentVideoWrapper${
              liveCaptions?.isEnabled ? ' has-live-captions' : ''
            }`}
          >
            <div className="wrapper">
              {!isFull && (
                <ErrorOutline
                  color="#dab420"
                  className="vidyo-info"
                  onClick={() => setShowInfo(!showInfo)}
                />
              )}
              {userId && providerType && videoScreen()}
              <div className="toggle-button-wrapper">
                <div className="media-controls">
                  <button
                    aria-label={sharedDeviceMessage(
                      'preCall.camera.label',
                      intl.locale || 'en',
                    )}
                    aria-pressed={cameraOpen}
                    className={`toggle-camera${cameraOpen ? '' : ' is-off'}`}
                    onClick={toggleCamera}
                    type="button"
                  >
                    {cameraOpen ? (
                      <VideocamOutlined />
                    ) : (
                      <VideocamOffOutlined />
                    )}
                  </button>
                  <button
                    aria-label={sharedDeviceMessage(
                      'preCall.microphone.label',
                      intl.locale || 'en',
                    )}
                    aria-pressed={audioOpen}
                    className={`toggle-mic${audioOpen ? '' : ' is-off'}`}
                    onClick={toggleMic}
                    type="button"
                  >
                    {audioOpen ? <MicNoneOutlined /> : <MicOffOutlined />}
                  </button>
                  {activeVideoProvider === 'vonage' &&
                    liveCaptions?.available && (
                      <button
                        aria-label={sharedDeviceMessage(
                          liveCaptions.isEnabled
                            ? 'liveCaptions.disable'
                            : 'liveCaptions.enable',
                          intl.locale || 'en',
                        )}
                        className="toggle-captions"
                        disabled={liveCaptions.isStarting}
                        onClick={toggleLiveCaptions}
                        title={sharedDeviceMessage(
                          liveCaptions.isEnabled
                            ? 'liveCaptions.disable'
                            : 'liveCaptions.enable',
                          intl.locale || 'en',
                        )}
                        type="button"
                      >
                        {liveCaptions.isEnabled ? (
                          <ClosedCaptionDisabledOutlined />
                      ) : (
                        <ClosedCaptionOutlined />
                        )}
                      </button>
                    )}
                </div>
                <div className="reconnect-controls">
                  <button
                    aria-label={intl.formatMessage({
                      defaultMessage: 'Reconnect',
                    })}
                    className="reconnect"
                    onClick={() => window.location.reload()}
                    type="button"
                  >
                    <SyncOutlined />
                    <p>
                      {intl.formatMessage({ defaultMessage: 'Reconnect' })}
                    </p>
                  </button>
                </div>
              </div>
            </div>
            <VideoBar
              countdown={countdown}
              disconnect={disconnectAll}
              toggleFullscreen={() => setIsFull(!isFull)}
              exitFullScreen={() => setIsFull(false)}
              receivers={receivers}
            />
          </div>
          {liveCaptions?.isEnabled && (
            <div className="practitioner-live-captions">
              <LiveCaptionsPanel
                captionState={liveCaptions.captionState}
                languageCode={liveCaptions.languageCode}
                onClose={liveCaptions.onClose}
                t={id =>
                  sharedDeviceMessage(id, intl.locale || 'en') || ''
                }
              />
            </div>
          )}
        </Fullscreen>
      </div>

      <div
        className="col-lg-5 col-md-12 col-sm-12"
        style={{ minHeight: '800px', paddingTop: 20 }}
      >
        <TwilioAudio appointment={appointment} />
        <Panel
          appointment={appointment}
          allAppointmentNotes={allAppointmentNotes}
          clientData={clientData}
          matchParamsID={match.params.id}
          assessments={assessments}
          showDeviceSettings={activeVideoProvider === 'vonage'}
          deviceSettings={vonageDeviceSettings}
          liveCaptions={liveCaptions}
          locale={intl.locale || 'en'}
        />
      </div>

      <Snackbar
        ContentProps={{ style: { background: '#92b4ff' } }}
        className="vidyo-snack"
        onClose={() => false}
        open={snackbarOpen}
        message={intl.formatMessage({
          defaultMessage: 'This session has expired and will close in 1 hour',
        })}
      />

      {resourceId && counsellorName && clientName && appointment.chat && (
        <TextChat
          toggle={() => setShowChatBox(!showChatBox)}
          resourceId={resourceId}
          clientID={clientID}
          open={showChatBox}
          timezone={timezone}
          receivers={receivers}
          clientName={clientName}
          appointment={appointment}
        />
      )}

      <InfoModal open={showInfo} handleClose={() => setShowInfo(false)} />
    </div>
  );
};

PractitionerVideo.propTypes = {
  fetchUser: PropTypes.func.isRequired,
  fetchAppointment: PropTypes.func.isRequired,
  fetchClient: PropTypes.func.isRequired,
  fetchAppointmentNotes: PropTypes.func.isRequired,
  updateAppointmentSession: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  appointment: PropTypes.object,
  intl: PropTypes.object.isRequired,
  clientData: PropTypes.object,
  me: PropTypes.object,
  clientID: PropTypes.number,
  allAppointmentNotes: PropTypes.array,
  updateAppointment: PropTypes.func.isRequired,
  clientName: PropTypes.string.isRequired,
  counsellorName: PropTypes.string.isRequired,
  fetchAppointmentAssessments: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const roles = _.get(state.user, 'roles');
  const appointment = _.get(state, 'session.appointment');
  const counsellor = _.get(appointment, 'counsellor');
  const client = _.get(appointment, 'client');

  return {
    appointment,
    roles,
    me: state.user,
    counsellorName: utils.nameParser(counsellor),
    clientName: utils.nameParser(client),
    clientID: client && client.id,
    clientData: client,
    allAppointmentNotes: state.session.appointment_notes,
    provider_yn: _.get(state.user, 'provider_yn'),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    updateAppointmentSession,
    fetchAppointment,
    fetchAppointmentNotes,
    fetchClient,
    fetchUser,
    fetchTodayAppointments,
    updateAppointment,
    fetchDocuments,
    fetchAppointmentAssessments,
  })(PractitionerVideo),
);
